import usePageTitle from "../utils/usePageTitle";
import React, { useContext, useEffect, useLayoutEffect, useRef } from "react";
import { ActivityPageContext } from "../components/dashboard/ActivityPageContext";
import { Users } from "lucide-react";
import UserFilterMultiSelect from "../components/UserFilterMultiSelect";
import ActivityScopeSwitch from "../components/dashboard/ActivityScopeSwitch";
import PersonalSessions from "../components/dashboard/PersonalSessionsBrowser";
import { useTranslation } from "react-i18next";

import { useLogin } from "../reducers";
import { useSettings } from "../utils/SettingsContext";
import useRecordingControls from "../utils/useRecordingControls";
import { RecentRecordingsSection } from "../components";
import { useUserFilterSelection } from "../utils/useUserFilterSelection";
import { RECORDING_USERS_HASH_KEY } from "./SessionsLayout";

const Recordings = () => {
  const { t } = useTranslation();
  const setPersonal = useContext(ActivityPageContext);
  const { autoAnalyze } = useSettings();
  const { user, permissions } = useLogin();
  const { recordingSyncStatus, recordingSyncCompletionId, handleRetryRecordingSync } =
    useRecordingControls();
  const { selectedUserIds, userOptions, handleUserChange, loadingUsers } = useUserFilterSelection(
    RECORDING_USERS_HASH_KEY,
    permissions.recordings.visualization,
  );

  const personal = !loadingUsers && selectedUserIds.length === 1 && selectedUserIds[0] === user.id;
  usePageTitle(t(personal ? "activity.my_title" : "activity.team_title"));
  useLayoutEffect(() => {
    setPersonal(personal);
  }, [personal, setPersonal]);
  // Team returns to the last selection that was not only you, or to everyone.
  const teamSelection = useRef<string[]>([]);
  useEffect(() => {
    if (!loadingUsers && !personal && selectedUserIds.length)
      teamSelection.current = selectedUserIds;
  }, [loadingUsers, personal, selectedUserIds]);
  // Only someone who can see other people has a team to switch to.
  const scopeControl = userOptions.length > 1 && (
    <ActivityScopeSwitch
      personal={personal}
      onChange={next =>
        handleUserChange(
          next
            ? [user.id]
            : teamSelection.current.length
              ? teamSelection.current
              : userOptions.map(option => option.id),
        )
      }
    />
  );
  const peopleControl = (
    <>
      {scopeControl}
      <div className="dashboard-people-filter">
        <UserFilterMultiSelect
          allLabel={t("watchlist.all_people")}
          icon={<Users size={16} />}
          value={selectedUserIds}
          options={userOptions}
          onChange={handleUserChange}
        />
      </div>
    </>
  );
  if (personal) return <PersonalSessions peopleControl={peopleControl} />;

  return (
    <div className="dashboard-page">
      <RecentRecordingsSection
        currentUserId={user.id}
        usersLoading={loadingUsers}
        activeUserIds={selectedUserIds}
        userOptions={userOptions}
        peopleControl={peopleControl}
        autoAnalyze={autoAnalyze}
        recordingSyncStatus={recordingSyncStatus}
        recordingSyncCompletionId={recordingSyncCompletionId}
        onRetryRecordingSync={handleRetryRecordingSync}
      />
    </div>
  );
};

export default Recordings;
